import { useCallback, useEffect, useState } from "react";
import {
  LiveParityRow,
  LocalSignalStatus,
  StrategyValidationStatus,
  QuarterlyReview,
  WeeklyParityStatus,
  api,
  isError,
} from "../api";

/** Overview panel for the local signal runner.
 *
 *  The runner evaluates the same strategy classes the backtester uses
 *  against fresh MT5 bars, so what it shows here is literally what the
 *  backtest would have seen. The preview submits nothing, which is the
 *  point: you can watch it for a fortnight alongside TradingView before
 *  letting it trade. */
export function LocalSignalsPanel() {
  const [status, setStatus] = useState<LocalSignalStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [validation, setValidation] = useState<Record<string, StrategyValidationStatus>>({});
  const [parity, setParity] = useState<LiveParityRow[] | null>(null);
  const [weekly, setWeekly] = useState<WeeklyParityStatus | null>(null);
  const [review, setReview] = useState<QuarterlyReview | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.localSignals();
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setError(null);
    setStatus(r);
    const v = await api.signalsValidationStatus();
    if (!isError(v)) {
      setValidation(Object.fromEntries(v.strategies.map((x) => [x.strategy, x])));
    }
    const w = await api.signalsParityWeekly();
    if (!isError(w)) setWeekly(w);
  }, []);

  async function loadReview() {
    setBusy(true);
    const r = await api.quarterlyReview();
    setBusy(false);
    if (isError(r)) {
      setNote(`Quarterly review failed: ${r.error}`);
      return;
    }
    setReview(r);
  }

  async function runWeekly() {
    setBusy(true);
    const r = await api.signalsParityWeeklyRun();
    setBusy(false);
    if (isError(r)) {
      setNote(`Weekly report failed: ${r.error}`);
      return;
    }
    setParity(r.strategies);
    setNote(`${r.subject}${r.notified ? " (sent)" : " (no notification channel delivered it)"}`);
    const w = await api.signalsParityWeekly();
    if (!isError(w)) setWeekly(w);
  }

  async function loadParity() {
    setBusy(true);
    const r = await api.signalsParityReport(7);
    setBusy(false);
    if (isError(r)) {
      setNote(`Backtest vs live failed: ${r.error}`);
      return;
    }
    setParity(r.strategies);
  }

  useEffect(() => {
    void refresh();
    // Each refresh costs one bars fetch per strategy, so this is slow on
    // purpose — the data only changes on a bar close anyway.
    const id = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  async function runNow() {
    if (
      !window.confirm(
        "Evaluate now and SUBMIT anything that fires? This can open a real position.",
      )
    ) {
      return;
    }
    setBusy(true);
    setNote(null);
    const r = await api.localSignalsRunNow();
    setBusy(false);
    if (isError(r)) {
      setNote(`Run failed: ${r.error}`);
      return;
    }
    const sent = r.results.filter((x) => x.signal !== null);
    setNote(
      sent.length === 0
        ? "Ran — nothing fired."
        : sent.map((x) => `${x.strategy} ${x.signal!.action.toUpperCase()}: ${x.outcome ?? "sent"}`).join(" · "),
    );
    void refresh();
  }

  if (error) {
    return (
      <section className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
        Local signals unavailable: {error}
      </section>
    );
  }
  if (status === null || status.strategies.length === 0) return null;

  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">
          Local signals
          <span className="ml-2 normal-case tracking-normal text-xs text-fg-muted">
            same code as the backtest, on live MT5 bars
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
              status.enabled ? "bg-success/20 text-success" : "bg-bg-hover text-fg-muted"
            }`}
          >
            {status.enabled ? `live · every ${status.interval_seconds}s` : "preview only"}
          </span>
          <button
            type="button"
            onClick={() => void runNow()}
            disabled={busy}
            className="rounded border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
          >
            {busy ? "running…" : "run now"}
          </button>
        </div>
      </div>

      {!status.enabled && (
        <p className="mb-2 text-xs text-fg-muted">
          The runner is off — this is what it <em>would</em> fire. Turn it on in
          Settings → Local signal runner once you are happy with it.
        </p>
      )}

      <table className="w-full text-xs font-mono">
        <thead className="text-fg-muted">
          <tr>
            <th className="p-1 text-left">strategy</th>
            <th className="p-1 text-left">signal</th>
            <th className="p-1 text-right">price</th>
            <th className="p-1 text-right">sl / tp</th>
            <th className="p-1 text-left">bar</th>
            <th className="p-1 text-left" title="Latest validation verdict and breaker state">
              validation
            </th>
          </tr>
        </thead>
        <tbody>
          {status.preview.map((row) => (
            <tr key={row.strategy} className="border-t border-border">
              <td className="p-1 text-fg">{row.strategy}</td>
              <td className="p-1">
                {row.signal ? (
                  <span
                    className={
                      row.signal.action === "buy" ? "text-success" : "text-danger"
                    }
                  >
                    {row.signal.action.toUpperCase()} {row.signal.symbol}
                  </span>
                ) : row.note ? (
                  // Could not even be evaluated - not the same as "no setup".
                  <span className="text-warn">not evaluated: {row.note}</span>
                ) : (
                  <span className="text-fg-muted">no setup</span>
                )}
              </td>
              <td className="p-1 text-right">{row.signal ? row.signal.price : "—"}</td>
              <td className="p-1 text-right text-fg-muted">
                {row.signal?.sl != null ? `${row.signal.sl} / ${row.signal.tp ?? "—"}` : "—"}
              </td>
              <td className="p-1 text-fg-muted">
                {row.signal ? new Date(row.signal.timestamp).toLocaleString() : ""}
              </td>
              <td className="p-1">
                <ValidationCell v={validation[row.strategy]} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {note && <p className="mt-2 text-xs text-fg-muted">{note}</p>}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => void loadParity()}
          disabled={busy}
          className="rounded border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
        >
          backtest vs live, last 7 days
        </button>
        <button
          type="button"
          onClick={() => void runWeekly()}
          disabled={busy}
          title="Run the weekly report now and send it to your notification channels"
          className="ml-2 rounded border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
        >
          send weekly report now
        </button>
        {weekly && (
          <p className="mt-1 text-[10px] text-fg-muted">
            Weekly report{" "}
            {weekly.enabled
              ? `on: ${WEEKDAYS[weekly.weekday] ?? weekly.weekday} ${String(weekly.hour_utc).padStart(2, "0")}:05 UTC`
              : "off (Settings → Weekly backtest-vs-live report)"}
            {weekly.last && (
              <>
                {" · last "}
                <span className={weekly.last.ok ? "text-success" : "text-warn"}>
                  {new Date(weekly.last.generated_at).toLocaleString()}: {weekly.last.subject}
                </span>
              </>
            )}
          </p>
        )}
        <button
          type="button"
          onClick={() => void loadReview()}
          disabled={busy}
          title="Each strategy's live profit factor over the last 12 months, and what the quarterly review would retire (changes nothing)"
          className="ml-2 rounded border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
        >
          quarterly review (preview)
        </button>
        {parity && <ParityTable rows={parity} />}
        {review && <ReviewTable review={review} />}
      </div>

      {status.enabled && status.last_run && (
        <p className={`mt-2 text-xs ${status.last_run.error ? "text-danger" : "text-fg-muted"}`}>
          {status.last_run.at
            ? `Last scheduled pass ${new Date(status.last_run.at).toLocaleTimeString()}`
            : "No scheduled pass has run since the backend started."}
          {status.last_run.error && ` — FAILED: ${status.last_run.error}`}
        </p>
      )}

      {status.recent && status.recent.length > 0 && (
        <div className="mt-3">
          <h3 className="mb-1 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
            Signals sent (since the backend started)
          </h3>
          <ul className="space-y-0.5 font-mono text-xs">
            {status.recent.map((s) => (
              // Unique per entry: strategy+bar alone repeated for every
              // pass that re-saw the same bar, and React then duplicated
              // and reordered rows on refresh.
              <li key={`${s.at}-${s.strategy}-${s.bar}`} className="flex flex-wrap gap-x-2">
                <span className="text-fg-muted">{new Date(s.at).toLocaleTimeString()}</span>
                <span className="text-fg">{s.strategy}</span>
                <span className={s.action === "buy" ? "text-success" : "text-danger"}>
                  {s.action.toUpperCase()} {s.symbol}
                </span>
                <span className={/refused|not sent|rejected|error/i.test(s.outcome) ? "text-danger" : "text-fg-muted"}>
                  {s.outcome}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}


function ValidationCell({ v }: { v: StrategyValidationStatus | undefined }) {
  if (!v || !v.validation) return <span className="text-fg-muted">not validated</span>;
  const ok = v.validation.verdict === "pass";
  return (
    <span className="flex flex-col">
      <span className={ok ? "text-success" : "text-danger"}>
        {v.validation.verdict} #{v.validation.id}
      </span>
      {v.breaker && (
        <span
          className={v.breaker.tripped ? "text-danger" : "text-fg-muted"}
          title={v.breaker.reason ?? undefined}
        >
          {v.breaker.tripped ? "breaker tripped" : "breaker ok"} · dd{" "}
          {v.breaker.drawdown_pct}%
          {v.breaker.drawdown_limit_pct !== null && ` / ${v.breaker.drawdown_limit_pct}%`}
        </span>
      )}
    </span>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ReviewTable({ review }: { review: QuarterlyReview }) {
  return (
    <div className="mt-2">
      <p className="mb-1 text-xs text-fg-muted">
        Live, last 12 months. Retires below profit factor{" "}
        {review.retire_below_profit_factor} with at least {review.min_trades} trades
        {review.enabled === false ? " (schedule off: Settings → Quarterly strategy review)" : ""}.
      </p>
      <table className="w-full text-xs font-mono">
        <thead className="text-fg-muted">
          <tr>
            <th className="p-1 text-left">strategy</th>
            <th className="p-1 text-right">trades</th>
            <th className="p-1 text-right">PF</th>
            <th className="p-1 text-right">net</th>
            <th className="p-1 text-left">review</th>
          </tr>
        </thead>
        <tbody>
          {review.strategies.map((r) => (
            <tr key={r.strategy} className="border-t border-border align-top">
              <td className="p-1 text-fg">{r.strategy}</td>
              <td className="p-1 text-right">{r.trades}</td>
              <td className="p-1 text-right">{r.profit_factor ?? "-"}</td>
              <td className="p-1 text-right">{r.net_pnl.toFixed(2)}</td>
              <td className={"p-1 " + (r.status === "retire" ? "text-danger" : "text-fg-muted")}>
                {r.status === "too_few_trades" ? "too few trades" : r.status}
                {r.action && <div className="text-[10px]">{r.action}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ParityTable({ rows }: { rows: LiveParityRow[] }) {
  return (
    <div className="mt-2">
      <p className="mb-1 text-xs text-fg-muted">
        Signals the strategy gave on MT5 bars vs signals the webhook received. Missing
        includes ones the runner skipped on purpose (position open, validation gate,
        breaker), so the match rate is a lower bound.
      </p>
      <table className="w-full text-xs font-mono">
        <thead className="text-fg-muted">
          <tr>
            <th className="p-1 text-left">strategy</th>
            <th className="p-1 text-right">expected</th>
            <th className="p-1 text-right">received</th>
            <th className="p-1 text-right">match</th>
            <th className="p-1 text-right">slippage avg / max (pips)</th>
            <th className="p-1 text-left">refused by</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.strategy} className="border-t border-border align-top">
              <td className="p-1 text-fg">
                {r.strategy}
                {(r.problems ?? []).filter(() => !r.error).map((p) => (
                  <div key={p} className="text-[10px] text-warn font-sans">
                    {p}
                  </div>
                ))}
              </td>
              {r.error ? (
                <td className="p-1 text-warn" colSpan={5}>
                  {r.error}
                </td>
              ) : (
                <>
                  <td className="p-1 text-right">{r.expected}</td>
                  <td className="p-1 text-right">{r.received}</td>
                  <td
                    className={
                      "p-1 text-right " +
                      (r.match_pct != null && r.match_pct < 95 ? "text-warn" : "")
                    }
                    title={[...(r.missing ?? []).map((m) => `missing ${m.action} ${m.bar_close}`),
                            ...(r.extra ?? []).map((m) => `extra ${m.action} ${m.bar_close}`)]
                      .join("\n")}
                  >
                    {r.match_pct == null ? "-" : `${r.match_pct}%`}
                  </td>
                  <td className="p-1 text-right">
                    {r.avg_slippage_pips ?? "-"} / {r.max_slippage_pips ?? "-"}
                  </td>
                  <td className="p-1 text-fg-muted">
                    {Object.entries(r.refused_by_rule ?? {})
                      .map(([k, n]) => `${k} ${n}`)
                      .join(", ") || "-"}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
